import React, { useEffect, useCallback, useState } from "react";
import { getSrc } from "gatsby-plugin-image";
import { useTranslation } from "react-i18next";

import {
  ArticleTemplate,
  ArticleHeader,
  SocialShare,
} from "../../components/ArticleTemplateComponents";

import PageProgressBar from "../../components/PageProgressBar";
import SubscribeBanner from "../../components/SubscribeBanner";

import { filterCategories, getAcfPartialContent } from "../../utils";
import { getInArticleBanner } from "../../client";

import "./style.scss";

const LearnArticleLayout = ({ data, lang, currentUrl, articleTopic }) => {
  const { t } = useTranslation();
  const categories = filterCategories(data.wpPost.categories.nodes, [
    articleTopic,
  ]);

  const [_headers, _setHeaders] = useState([]);

  const createBanner = ({ image_url, is_show_on_article, url_params }) => {
    if (!is_show_on_article || image_url === "") return null;

    const elemBanner = document.createElement("a");
    elemBanner.className = "article-learn__banner my-4";
    elemBanner.setAttribute(
      "href",
      `${t("common.exchangeLink")}/accounts/sign-up${
        url_params ? `?${url_params}` : ""
      }`
    );

    elemBanner.innerHTML = `<img src="${image_url}" alt=""/>`;

    return elemBanner;
  };

  const prepareBanners = useCallback(async (elemContent, elemMiddleHeader) => {
    const banners = await getInArticleBanner(lang);

    if (!banners) return;

    const topBanner = createBanner(banners.top);
    const bottomBanner = createBanner(banners.bottom);

    topBanner && elemContent.before(topBanner);
    bottomBanner && elemContent.after(bottomBanner);

    if (elemMiddleHeader) {
      const middleBanner = createBanner(banners.middle);
      middleBanner && elemMiddleHeader.before(middleBanner);
    }
  }, []);

  const prepareContentHeaders = (elemHeaders) => {
    const headerText = [];

    Object.values(elemHeaders).forEach((elemHeader, i) => {
      elemHeader.setAttribute(`id`, `header-c${i}`);
      headerText.push(elemHeader.textContent || elemHeader.innerHTML || "");
    });

    _setHeaders(headerText);
  };

  useEffect(() => {
    const elemContent = document.getElementById("article-learn__content");
    elemContent.innerHTML = data.wpPost.content;

    const elemHeaders = elemContent.getElementsByTagName("h2");

    const elemMiddleHeader =
      elemHeaders.length > 1 ? elemHeaders[(elemHeaders.length / 2) | 0] : null;

    prepareBanners(elemContent, elemMiddleHeader);
    prepareContentHeaders(elemHeaders);
  }, [data.wpPost.content, prepareBanners]);

  const [articleHeight, parentArticleHeight] = useState(0);

  return (
    <>
      <PageProgressBar articleHeight={articleHeight} />
      <ArticleTemplate
        relatedArticleTopic={articleTopic}
        relatedArticles={data.relatedArticles.nodes}
        contentClassNames="learn-article"
        childrenContainerClassNames="grid-col-12"
        articleHeight={parentArticleHeight}
      >
        <div className="grid-container">
          <div className="grid-col-xl-2 grid-col-12">
            <div className="table-of-contents--sticky">
              <p>
                <strong>{t("learn.tableOfContents")}</strong>
              </p>
              <ul id="article-learn__index" className="mb-5">
                {_headers.map((header, i) => (
                  <li className="mb-2" key={`article-learn__index-${i}`}>
                    <a href={`#header-c${i}`}>{header}</a>
                  </li>
                ))}
              </ul>
              <SocialShare
                classNames="grid-col-4 grid-col-lg-1"
                url={currentUrl}
                hasTitleText={false}
              />
            </div>
          </div>
          <div className="grid-col-xl-10 grid-col-12">
            <ArticleHeader
              title={data.wpPost.title}
              date={data.wpPost.date}
              topic={articleTopic}
              categories={categories}
            />
            <div className="grid-container">
              <div className="article-learn__featured-image mt-5 grid-col-12">
                <img
                  className="img-fluid"
                  src={getSrc(
                    data.wpPost.featuredImage.node.localFile.childImageSharp
                      .gatsbyImageData
                  )}
                  alt=""
                />
                <div
                  className="article-learn__featured-image-caption grid-col-12 d-flex justify-content-center text-center"
                  dangerouslySetInnerHTML={{
                    __html: data.wpPost.featuredImage.node.caption,
                  }}
                ></div>
              </div>
              <div className="table-of-contents grid-col-12">
                <SocialShare
                  classNames="grid-col-4 grid-col-lg-1 mb-5"
                  url={currentUrl}
                  hasTitleText={false}
                />
                <p>
                  <strong>{t("learn.tableOfContents")}</strong>
                </p>
                <ul id="article-learn__index">
                  {_headers.map((header, i) => (
                    <li className="mb-4" key={`article-learn__index-${i}`}>
                      <a href={`#header-c${i}`}>{header}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="article-learn__content-wrapper grid-col-12 grid-col-lg-10">
                <div id="article-learn__content" />
              </div>
            </div>
          </div>
        </div>
      </ArticleTemplate>
      <SubscribeBanner
        content={getAcfPartialContent(data.subscribeBannerContent)}
      />
    </>
  );
};

export default LearnArticleLayout;
